import React, { memo, useMemo } from 'react';
import Tabs, { TabPane } from 'rc-tabs';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useRequest } from 'ahooks';
import { Button, message, Tooltip } from 'antd';
import Dot from '@/components/Dot';
import Chart from '@/business-components/chart';
import ChartHeader from '@/business-components/chart-header';
import Rate from '@/business-components/Rate';
import InfoCard from '@/business-components/InfoCard';
import 'rc-tabs/assets/index.css';
import TitleTag from '@/components/titletag';
import { RootState } from '@/models';
import { ConBrandDetailsWebController } from '@/services/swagger/ConBrandDetailsWebController';
import LogoWithText from '@/business-components/LogoWithText';
import styles from './index.module.scss';

const STARTENUM = {
  5: '5星数量',
  4: '4星数量',
  3: '3星数量',
  2: '2星数量',
  1: '1星数量',
};

const brandDetailController = new ConBrandDetailsWebController();

const SCORE_TYPE_MAP = {
  company_background: {
    name: '公司背景',
  },
  group_capacity: {
    name: '核心团队信息',
  },
  supply_chain: {
    name: '供应链管理能力',
  },
};
export type InvestorScoreProps = {
  /** 品牌id */
  conBrandId?: string;
  /** 显示公司背景评分模块 */
  showCompanyScore?: boolean;
  /** 显示团队能力评分模块 */
  showGroupScore?: boolean;
  /** 显示供应链完善度评分模块 */
  showSupplyChainScore?: boolean;
  /** 显示打分模块 */
  showRate?: boolean;
};
const InvestorScore: React.FC<InvestorScoreProps> = ({
  conBrandId,
  showCompanyScore,
  showGroupScore,
  showSupplyChainScore,
  showRate,
}) => {
  const dispatch = useDispatch();
  /** 品牌评分信息 */
  const { data: investorScoreData } = useRequest(async () => {
    const res = await brandDetailController.conBrandDetailsWebControllerV1BrandScoreUsingGet1({ conBrandId });
    return res.data.data;
  });

  const {
    data: brandScoreList = [
      { scoreType: 'company_background', score: null },
      { scoreType: 'group_capacity', score: null },
      { scoreType: 'supply_chain', score: null },
    ],
    mutate,
  } = useRequest(async () => {
    const res = await brandDetailController.conBrandDetailsWebControllerV1BrandScoreByUserUsingGet1({
      conBrandId,
    });
    return res?.data?.data?.list?.length
      ? res?.data?.data?.list
      : [
          { scoreType: 'company_background', score: null },
          { scoreType: 'group_capacity', score: null },
          { scoreType: 'supply_chain', score: null },
        ];
  });
  const { run } = useRequest(
    async () => {
      const res = await brandDetailController.conBrandDetailsWebControllerV1UserBrandScoreUsingPost1({
        conBrandId,
        brandScoreList,
      });
      if (res.data.code === 200) {
        message.success('评分成功');
        dispatch({ type: 'modal/close' });
      } else {
        message.error(res.data.msg);
      }
    },
    {
      manual: true,
    }
  );
  // const disabled = useMemo(() => brandScoreList.every(item => item.score <= 0), [brandScoreList]);
  const { memberInfo } = useSelector((state: RootState) => state.user);
  /** 公司背景评分 */
  const companyRateDetail = useMemo(
    () =>
      investorScoreData?.companyScore?.fiveStarLevelNum !== null &&
      investorScoreData?.companyScore?.fiveStarLevelNum !== undefined
        ? [
            { label: STARTENUM[5], value: investorScoreData?.companyScore.fiveStarLevelNum },
            { label: STARTENUM[4], value: investorScoreData?.companyScore.fourStarLevelNum },
            { label: STARTENUM[3], value: investorScoreData?.companyScore.threeStarLevelNum },
            { label: STARTENUM[2], value: investorScoreData?.companyScore.twoStarLevelNum },
            { label: STARTENUM[1], value: investorScoreData?.companyScore.oneStarLevelNum },
          ]
        : [],
    [
      investorScoreData?.companyScore.fiveStarLevelNum,
      investorScoreData?.companyScore.fourStarLevelNum,
      investorScoreData?.companyScore.oneStarLevelNum,
      investorScoreData?.companyScore.threeStarLevelNum,
      investorScoreData?.companyScore.twoStarLevelNum,
    ]
  );
  /** 核心团队信息评分 */
  const groupRateDetail = useMemo(
    () =>
      investorScoreData?.groupScore?.fiveStarLevelNum !== null &&
      investorScoreData?.groupScore?.fiveStarLevelNum !== undefined
        ? [
            { label: STARTENUM[5], value: investorScoreData?.groupScore.fiveStarLevelNum },
            { label: STARTENUM[4], value: investorScoreData?.groupScore.fourStarLevelNum },
            { label: STARTENUM[3], value: investorScoreData?.groupScore.threeStarLevelNum },
            { label: STARTENUM[2], value: investorScoreData?.groupScore.twoStarLevelNum },
            { label: STARTENUM[1], value: investorScoreData?.groupScore.oneStarLevelNum },
          ]
        : [],
    [
      investorScoreData?.groupScore.fiveStarLevelNum,
      investorScoreData?.groupScore.fourStarLevelNum,
      investorScoreData?.groupScore.oneStarLevelNum,
      investorScoreData?.groupScore.threeStarLevelNum,
      investorScoreData?.groupScore.twoStarLevelNum,
    ]
  );
  const supplyChainRateDetail = useMemo(
    () =>
      investorScoreData?.supplyChainScore?.fiveStarLevelNum !== null &&
      investorScoreData?.supplyChainScore?.fiveStarLevelNum !== undefined
        ? [
            { label: STARTENUM[5], value: investorScoreData?.supplyChainScore.fiveStarLevelNum },
            { label: STARTENUM[4], value: investorScoreData?.supplyChainScore.fourStarLevelNum },
            { label: STARTENUM[3], value: investorScoreData?.supplyChainScore.threeStarLevelNum },
            { label: STARTENUM[2], value: investorScoreData?.supplyChainScore.twoStarLevelNum },
            { label: STARTENUM[1], value: investorScoreData?.supplyChainScore.oneStarLevelNum },
          ]
        : [],
    [
      investorScoreData?.supplyChainScore.fiveStarLevelNum,
      investorScoreData?.supplyChainScore.fourStarLevelNum,
      investorScoreData?.supplyChainScore.oneStarLevelNum,
      investorScoreData?.supplyChainScore.threeStarLevelNum,
      investorScoreData?.supplyChainScore.twoStarLevelNum,
    ]
  );

  return (
    <div className={styles.investors}>
      {showCompanyScore && (
        <div className={styles.card}>
          <Chart
            title={
              <div className={classNames('flex-row-nowrap', 'flex-start-center', styles.title)}>
                <Dot className={styles.dot} />
                <span>公司背景</span>
              </div>
            }
            panelClassName={styles.cardColor}
          >
            <Rate
              allowHalf
              value={investorScoreData?.companyScore.score}
              rateDetail={companyRateDetail}
              disabled
              className={styles.rate}
              describe={
                <>
                  <span className={styles.score}>{investorScoreData?.companyScore.score}</span>
                  <span className={styles.explanation}>（注：满分5分，若无星则当前暂无投资人评分）</span>
                </>
              }
            />
            <div className={styles.companyDesc}>{investorScoreData?.companyScore.parentCompanyDesc}</div>
          </Chart>
        </div>
      )}
      {showGroupScore && (
        <div className={`${styles.coreTeam} ${styles.card}`}>
          <Chart
            title={
              <div className={classNames('flex-row-nowrap', 'flex-start-center', styles.title)}>
                <Dot className={styles.dot} />
                <span>核心团队信息</span>
              </div>
            }
            panelClassName={styles.cardColor}
          >
            <Rate
              allowHalf
              value={investorScoreData?.groupScore.score}
              rateDetail={groupRateDetail}
              disabled
              className={styles.rate}
              describe={
                <>
                  <span className={styles.score}>{investorScoreData?.groupScore.score}</span>
                  <span className={styles.explanation}>（注：满分5分，若无星则当前暂无投资人评分）</span>
                </>
              }
            />
            {!!investorScoreData?.groupScore?.conBrandGroupList?.length && (
              <Tabs tabPosition="left">
                {investorScoreData?.groupScore?.conBrandGroupList?.map((item, index) => (
                  <TabPane
                    key={index}
                    tab={
                      <div className={classNames('flex-row-nowrap', 'flex-start-center', styles.people)}>
                        <LogoWithText
                          src={item.avatarUrl}
                          spanClass={styles.headImg}
                          logoClass={styles.headImg}
                          width={40}
                          borderRadios={100}
                          len={1}
                          chName={item.memberName}
                        />
                        <div className={classNames('flex-column-nowrap', 'flex-between-start', styles.user)}>
                          <div className={styles.name}>{item.memberName}</div>
                          <Tooltip title={item.memberJob}>
                            <div className={styles.job}>{item.memberJob}</div>
                          </Tooltip>
                        </div>
                      </div>
                    }
                  >
                    <div className={styles.tabContent}>
                      <TitleTag style={{ marginBottom: '10px' }}>简介</TitleTag>
                      <div style={{ height: 210, overflowY: 'scroll' }}>{item?.memberDesc}</div>
                    </div>
                  </TabPane>
                ))}
              </Tabs>
            )}
          </Chart>
        </div>
      )}
      {showSupplyChainScore && (
        <div className={`${styles.supplyChain} ${styles.card}`}>
          <Chart
            title={
              <div className={classNames('flex-row-nowrap', 'flex-start-center', styles.title)}>
                <Dot className={styles.dot}></Dot>
                <span>供应链完善度</span>
              </div>
            }
            panelClassName={styles.cardColor}
          >
            <Rate
              allowHalf
              value={investorScoreData?.supplyChainScore.score}
              rateDetail={supplyChainRateDetail}
              disabled
              className={styles.rate}
              describe={
                <>
                  <span className={styles.score}>{investorScoreData?.supplyChainScore.score}</span>
                  <span className={styles.explanation}>（注：满分5分，若无星则当前暂无投资人评分）</span>
                </>
              }
            />
            <div className={classNames('flex-row-nowrap', 'flex-start-center', styles.supplyChain__conetnt)}>
              {investorScoreData?.supplyChainScore.selfFactoryNum && (
                <InfoCard
                  className={styles.supplyChainInfoCard}
                  iconXLinkHref="#icon-product_amount"
                  title="自主生产能力"
                  info={
                    <span className={styles.supplyChainInfoNum}>
                      {investorScoreData?.supplyChainScore.selfFactoryNum}
                    </span>
                  }
                />
              )}
              {investorScoreData?.supplyChainScore.storeNum && (
                <InfoCard
                  className={styles.supplyChainInfoCard}
                  iconXLinkHref="#icon-product_amount"
                  title="仓储能力"
                  info={
                    <span className={styles.supplyChainInfoNum}>{investorScoreData?.supplyChainScore.storeNum}</span>
                  }
                />
              )}
            </div>
          </Chart>
        </div>
      )}
      {showRate && memberInfo.hasInvestorUser && (
        <div className={styles.mypoint}>
          <ChartHeader
            title={
              <div className={styles.title}>
                综合指数雷达图<span className={styles.tips}>注：提交评分可多解锁一次品牌（上限30）</span>
              </div>
            }
            titleFontSize={20}
            dateSubTitle={false}
          />
          {brandScoreList.map(item => {
            return (
              <div
                key={item.scoreType}
                className={classNames('flex-row-nowrap', 'flex-start-center', styles.pointIndex)}
              >
                <div className={classNames('flex-row-nowrap', 'flex-start-center', styles.pointIndex__Name)}>
                  <Dot className={styles.dot} />
                  {SCORE_TYPE_MAP[item.scoreType].name}
                </div>
                <Rate
                  value={item.score}
                  // allowHalf
                  onChange={value =>
                    mutate(
                      brandScoreList.map(i => {
                        return {
                          scoreType: i.scoreType,
                          score: i.scoreType === item.scoreType ? value : i.score,
                        };
                      })
                    )
                  }
                  style={{ color: '#f75c2f', fontSize: '40px' }}
                />
              </div>
            );
          })}
          <Button className={styles.submitBtn} type="primary" size="large" onClick={run}>
            提交打分
          </Button>
        </div>
      )}
    </div>
  );
};
export default memo(InvestorScore);
